// ==============================================
// Rankings Page
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { metadataAPI, rankingsAPI } from '../services/api';
import { PlayerLevel, RankingEntry } from '../types';
import { playerPath } from '../utils/slug';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

function playerInitials(entry: RankingEntry) {
  return `${entry.first_name?.[0] ?? ''}${entry.last_name?.[0] ?? ''}`.toUpperCase();
}

const RankingAvatar: React.FC<{ entry: RankingEntry }> = ({ entry }) => {
  const src = entry.avatar_url ? `${SITE_URL}${entry.avatar_url}` : null;
  const name = `${entry.first_name} ${entry.last_name}`.trim();
  return (
    <div style={{
      width: 42,
      height: 42,
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: src ? 'transparent' : 'linear-gradient(135deg, var(--tt-primary), var(--tt-accent))',
      color: '#fff',
      fontWeight: 900,
      border: '2px solid var(--tt-surface)',
      boxShadow: '0 2px 10px rgba(15,23,42,.12)',
    }}>
      {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : playerInitials(entry)}
    </div>
  );
};

const MpTeaser: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: compact ? '.35rem' : '.55rem',
    border: '1px solid var(--tt-border)',
    background: compact ? 'rgba(148,163,184,.08)' : 'linear-gradient(135deg, rgba(239,246,255,.9), rgba(255,255,255,.96))',
    borderRadius: compact ? 999 : 12,
    padding: compact ? '.2rem .55rem' : '.7rem .9rem',
    color: 'var(--tt-text-muted)',
    fontSize: compact ? '.72rem' : '.82rem',
    fontWeight: compact ? 800 : 700,
  }}>
    <span style={{ filter: 'blur(2px)', color: 'var(--tt-primary)', fontWeight: 950 }}>MP 000</span>
    {!compact && <span>MP va fi afișat mai târziu, după ce strângem mai multe informații. Momentan nu facem calcule.</span>}
  </div>
);

const Rankings: React.FC = () => {
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [levels, setLevels] = useState<PlayerLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  const getLevelBadgeClass = (slug?: string | null) => {
    switch (slug) {
      case 'elite':         return 'cat-elite';
      case 'profesionisti': return 'cat-elite';
      case 'open':          return 'cat-a';
      case 'avansati':      return 'cat-b';
      case 'hobby':         return 'cat-c';
      case 'amator':        return 'cat-d';
      default:              return 'cat-d';
    }
  };

  const getRankMedal = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (selectedLevel) params.level = selectedLevel;

      const response = await rankingsAPI.getAll(params);

      if (response.success && response.data) {
        setRankings(response.data.rankings || []);
      } else {
        setError(response.message || 'Failed to load rankings');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedLevel]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    metadataAPI.getAll()
      .then(res => {
        if (res.success && res.data) {
          setLevels([...res.data.player_levels].sort((a, b) => a.rank_order - b.rank_order));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="page-header">
          <h2>Clasamente jucatori</h2>
          <p>Clasament calculat pe baza victoriilor din meciuri inregistrate</p>
        </div>

        <div className="entity-card mb-4">
          <div className="entity-card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 900 }}>Filtrează după nivel</h5>
                <div style={{ color: 'var(--tt-text-muted)', fontSize: '.84rem' }}>Afișează doar jucătorii din nivelul selectat.</div>
              </div>
              <MpTeaser />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              <button
                type="button"
                onClick={() => setSelectedLevel('')}
                style={{
                  border: `1.5px solid ${selectedLevel === '' ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                  background: selectedLevel === '' ? 'var(--tt-primary)' : 'var(--tt-surface)',
                  color: selectedLevel === '' ? '#fff' : 'var(--tt-text-2)',
                  borderRadius: 999,
                  padding: '.4rem .85rem',
                  fontWeight: 800,
                  fontSize: '.82rem',
                }}
              >
                Toate nivelurile
              </button>
              {levels.map(level => {
                const active = selectedLevel === level.slug;
                return (
                  <button
                    key={level.id}
                    type="button"
                    onClick={() => setSelectedLevel(level.slug)}
                    style={{
                      border: `1.5px solid ${active ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                      background: active ? '#eff6ff' : 'var(--tt-surface)',
                      color: active ? 'var(--tt-primary)' : 'var(--tt-text-2)',
                      borderRadius: 999,
                      padding: '.4rem .85rem',
                      fontWeight: 800,
                      fontSize: '.82rem',
                    }}
                  >
                    {level.display_name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {loading && (
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Se incarca...</span></div>
            <span>Se incarca clasamentele...</span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          rankings.length === 0 ? (
            <div className="tt-empty">
              <div className="tt-empty-icon">📊</div>
              <h5>Niciun jucator gasit</h5>
              <p>Incearca sa ajustezi filtrul de nivel</p>
            </div>
          ) : (
            <div className="entity-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Loc</th>
                      <th>Jucator</th>
                      <th>Nivel</th>
                      <th>Victorii</th>
                      <th>Meciuri</th>
                      <th>MP</th>
                      <th>Rata V</th>
                      <th>Turnee</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((entry, index) => (
                      <tr key={entry.user_id}>
                        <td>
                          <strong className="fs-5">{getRankMedal(entry.rank ?? index + 1)}</strong>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', minWidth: 220 }}>
                            <Link to={playerPath({ id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name })} style={{ textDecoration: 'none' }}>
                              <RankingAvatar entry={entry} />
                            </Link>
                            <div style={{ minWidth: 0 }}>
                              <Link
                                to={playerPath({ id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name })}
                                style={{ color: 'var(--tt-text)', textDecoration: 'none', fontWeight: 900, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {entry.first_name} {entry.last_name}
                              </Link>
                              <div style={{ color: 'var(--tt-text-muted)', fontSize: '.75rem' }}>Profil jucător</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {entry.level_name
                            ? <span className={`badge ${getLevelBadgeClass(entry.level_slug)}`}>{entry.level_name}</span>
                            : <span className="text-muted">—</span>
                          }
                        </td>
                        <td><strong className="text-primary">{entry.total_wins}</strong></td>
                        <td>{entry.total_matches}</td>
                        <td><MpTeaser compact /></td>
                        <td>{Number(entry.win_rate ?? 0).toFixed(1)}%</td>
                        <td>{entry.tournaments_played}</td>
                        <td>
                          <Link
                            to={playerPath({ id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name })}
                            className="btn btn-sm btn-outline-primary"
                          >
                            Profil
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Rankings;
